import { Injectable, inject } from '@angular/core';
import { GmrUserInfo } from './gmr.models';
import { COOKIE_DOMAINS, cookieDomainOfHost } from './iwid-cookie.service';
import { MatomoService } from './matomo.service';

//
// The host labels a session may span under either of our registrable domains, derived on 2026-08-18
// from the deployment mapping of the `web` repository and the configured hosts of the .NET
// applications. The list handed to Inspectlet is built for the domain the page is served from: the
// identity cookie is written per domain, so naming the other domain's hosts would promise a continuity
// the cookie can not deliver, and one fixed `.invantive.com` list was wrong on every invantive.eu host.
// A host under neither domain gets the first domain's list, which is what stood here before. The same
// labels live in `invantive-identity.js` in every other application; see itgen/ai/IWID_IDENTITY.md.
//
const CROSS_DOMAIN_HOST_LABELS =
[ 'cloud'
, 'bridge-online'
, 'mcp'
, 'cs'
, 'access-odata'
, 'currency-toolkit'
, 'valuta-tools'
, 'valuta-tools-app'
, 'get-my-report'
, 'gmrappnl'
, 'gmrappbe'
, 'documentation'
, 'download'
, 'releasenotes'
, 'static'
, 'clientredirect'
, 'forums'
];

export function crossDomainHosts(host: string): string[]
{
  const domain = cookieDomainOfHost(host) ?? COOKIE_DOMAINS[0];

  return [domain].concat(CROSS_DOMAIN_HOST_LABELS.map(label => label + '.' + domain));
}

//
// What is known about a failed backend call at the moment it is reported. The AngularJS original
// tagged the response *body* only (MessageCode/Message), which is empty for precisely the failures
// that carry no body - a dropped connection, a gateway page, an HTML login redirect - and then wrote
// a telemetry entry consisting of two undefined values and nothing to act on. Status, method and URL
// are already in the HttpErrorResponse the interceptor holds, so carrying them along costs nothing.
//
export interface HttpFailureContext
{
  method: string;
  url: string | null;
  status: number;

  //
  // The Data Access Point's own ID of the failed request, the key to its trace.
  //
  requestId?: string | null;
}

//
// The response header in which the Data Access Point returns the ID of every request it handled.
//
export const REQUEST_ID_HEADER = 'X-Invantive-Request-Id';

//
// Thin pokes into untyped vendor globals (Inspectlet, Matomo), plus the one
// place where an application event is handed to more than one of them. There is no
// Angular-idiomatic replacement for third-party vendor scripts - this service exists purely to
// move the same guarded window.* calls out of a component and off jQuery.
//
@Injectable({ providedIn: 'root' })
export class ThirdPartyIntegrationsService
{
  private readonly matomo = inject(MatomoService);

  initAll(userInfo: GmrUserInfo, iwid: string | undefined): void
  {
    this.initMatomo(iwid);
    this.initInspectlet(userInfo, iwid);
    this.pushFunnelConversion(userInfo);
  }

  //
  // The first log-on of a new user is the conversion of the funnel before the IWID
  // (itgen/ai/design/concept/20260929_PRE_IWID_FUNNEL_MEASUREMENT.md, S-4). A data-layer event rather
  // than a tracker call, because the tag manager decides which property records it.
  //
  private pushFunnelConversion(userInfo: GmrUserInfo): void
  {
    if (String(userInfo.first_log_on).toLowerCase() !== 'true')
    {
      return;
    }

    window._mtm = window._mtm ?? [];
    window._mtm.push({ event: 'signup_success', plan: 'get-my-report' });
  }

  //
  // Hand the visitor's identity to Matomo, which is what makes a returning visitor recognisable at all
  // here - see MatomoService.setUserId for why there is no cookie doing that job.
  //
  // The prefix test is the point of this method, not a formality. `d2d_user_identity`, which is what
  // IwidCookieService returns, is a union of two kinds of identity built in gmr-functions.sql:
  //
  //   case when l_user_iwid is not null then 'IWID-' || to_char(l_user_iwid)
  //                                     else 'EML-'  || l_user_email_address end
  //
  // and `gmr_ssn_users.iwid` is nullable (ddl/tables.sql), read through a `max()` that yields null when
  // no row matches - so the second branch is reachable, twice over. A Matomo User ID is stored with
  // every visit it identifies, so letting the `EML-` form through would put customer e-mail addresses
  // in the analytics data permanently and turn a pseudonymous data set into a directly identifying one.
  // That is the one property of this Matomo installation worth protecting: it is the only vendor here
  // that stores nothing identifying client-side.
  //
  // The `IWID-` prefix is kept rather than stripped. It is opaque, so it costs nothing, and it makes
  // plain in Matomo's own reports which kind of identifier is being looked at.
  //
  private initMatomo(iwid: string | undefined): void
  {
    if (iwid?.startsWith('IWID-'))
    {
      this.matomo.setUserId(iwid);
    }
  }

  private initInspectlet(userInfo: GmrUserInfo, iwid: string | undefined): void
  {
    try
    {
      this.queue().push(['identify', userInfo.usr_email_address]);

      //
      // The hosts a session may span, so Inspectlet stitches a visit that moves between our
      // applications instead of counting it twice. An empty list, which is what stood here originally,
      // leaves the default of the current host only. Built for the domain of the page, see
      // crossDomainHosts above.
      //
      this.queue().push(['crossDomain', crossDomainHosts(window.location.hostname)]);

      //
      // Only tag the IWID when there is one. The original pushed the key unconditionally, so a
      // session without an IWID got a tag whose value was undefined - which reads in the dashboard
      // as a value rather than as its absence, and is indistinguishable from a bug in resolving it.
      //
      if (iwid)
      {
        this.tagSession({ iwid });
      }
      this.tagSession({ 'product-last-used': 'get-my-report', 'product-get-my-report': null });
    }
    catch (e)
    {
      console.error(e);
    }
  }

  //
  // Crisp is deliberately not told who the user is: only Invantive Cloud registers anyone at Crisp,
  // with a signed address (itgen/ai/design/concept/20260929_CRISP_IDENTITY_AND_SESSION_CONTINUITY.md).
  // A session Cloud registered earlier in this browser is reused here through Crisp's own cookie.
  //
  //
  // A screen change, reported to both vendors that can express one.
  //
  // Inspectlet gets it as a virtual page: a session recording of this application is otherwise one
  // unbroken take from log-on to close, with no way to jump to the moment a report was opened.
  //
  trackNavigation(path: string, title: string): void
  {
    this.matomo.trackVirtualPageView(path, title);

    this.queue().push(['virtualPage']);
  }

  //
  // An application event. Matomo gets a real event, which is what a goal or a funnel step can be
  // defined on; Inspectlet has no event concept and gets a session tag, which is what its recordings
  // can be filtered by. The tag keys keep the `getmyreport-` prefix the existing Inspectlet tags in
  // this file already use, so all of them sort together in Inspectlet's own interface.
  //
  trackEvent(category: string, action: string, name?: string): void
  {
    this.matomo.trackEvent(category, action, name);

    this.tagSession({ [`getmyreport-${category}-${action}`.toLowerCase().replace(/\s+/g, '-')]: name ?? '' });
  }

  //
  // A report download. `url` must already be sanitised by the caller: the real request URL carries
  // every parameter value the user filled in, which has no business being in an analytics report.
  //
  trackDownload(url: string, presetName: string): void
  {
    this.matomo.trackDownload(url);
    this.trackEvent('Report', 'Download started', presetName);
  }

  scanForms(): void
  {
    this.matomo.scanForForms();
  }

  //
  // 1:1 port of the errorInterceptor's own vendor-reporting block (gmr-invantive.js). The
  // original reads errorData.MessageCode/.Message unguarded by an `if (errorData)` check here
  // (that guard only wraps the DOM-write block above it) - given a falsy errorData this would
  // throw inside the AngularJS $translate().then() callback, silently becoming an unhandled
  // promise rejection with no other effect. Reproducing that exact crash here would instead
  // break the RxJS chain that still needs to forward the real HTTP error downstream, which is a
  // strictly worse regression than today's behaviour - so this is deliberately guarded, unlike
  // the original. See migration notes.
  //
  reportInterceptorError(errorData: { MessageCode?: string; Message?: string } | null | undefined, request: HttpFailureContext | null): void
  {
    this.reportError(errorData?.MessageCode ?? 'xxgmr113', this.describeFailure(errorData?.Message, request), request?.requestId);
  }

  //
  // Failures the interceptor does not turn into an error modal, because this application has no
  // message for the status: in practice 0 (connection dropped, or the request blocked by an
  // extension) and the gateway range 502/503/504. Those were not reported at all before - no tag, no
  // APM entry - so a backend outage produced no telemetry whatsoever, at the one moment it is worth
  // having. Nothing changes for the user: whichever call site handles the error still owns it.
  //
  reportUnhandledHttpFailure(request: HttpFailureContext): void
  {
    this.reportError('xxgmr113', this.describeFailure('Unhandled backend failure, no error dialog shown.', request), request.requestId);
  }

  //
  // 1:1 port of showHttpGetError's vendor-reporting block (gmr-app.js).
  //
  reportHttpError(messageText: string, msgCode: string, requestId?: string | null): void
  {
    this.reportError(msgCode, messageText, requestId);
  }

  //
  // 1:1 port of the parameter-count-mismatch reporting in startParameters (gmr-app.js).
  //
  reportGenericError(message: string, code: string): void
  {
    this.reportError(code, message);
  }

  //
  // One shape for every error path. There used to be three: this one wrote "getmyreport-error-
  // message" plus a per-code marker key, the two HTTP paths wrote "getmyreport-error-message-code"
  // and "-text", and nothing wrote both - so no single query in Inspectlet found all errors.
  //
  // Everything now writes the stable pair *and* the marker, which are complementary rather than
  // redundant: tagSession overwrites a key it has already seen, so the pair only ever describes the
  // LAST error of a session, while the marker keeps every distinct code that occurred visible.
  //
  private reportError(tagCode: string, text: string, requestId?: string | null): void
  {
    //
    // The code is the action, so Matomo counts errors per code; the request ID is the name, which is
    // the key to the trace of the Data Access Point and is absent when the failure never reached it.
    //
    this.matomo.trackEvent('Error', tagCode, requestId ? requestId : undefined);

    this.tagSession({ 'getmyreport-error-message-code': tagCode, 'getmyreport-error-message-text': text });
    this.tagSession({ [`getmyreport-error-code-${tagCode}`]: '' });
  }

  //
  // A description that stays useful when the response body is empty - which is the normal case for
  // the failures that matter most, and the case in which this used to report two undefined values.
  //
  private describeFailure(message: string | undefined, request: HttpFailureContext | null): string
  {
    const parts: string[] = [];

    if (message)
    {
      parts.push(message);
    }
    if (request)
    {
      parts.push(`HTTP ${request.status} on ${request.method} ${request.url ?? 'unknown url'}`);
    }

    return parts.length > 0 ? parts.join(' - ') : 'Backend failure without any further detail.';
  }

  //
  // Inspectlet's snippet is a plain array that the SDK drains once it loads, so anything pushed
  // beforehand is replayed - provided the array exists. Creating it here is therefore not a
  // workaround but the documented way to tag early.
  //
  // It is also what the code this replaces got wrong: the queue was created during init only, while
  // every other call site guarded on `if (window.__insp)` and the error paths wrote
  // `window.__insp = window.__insp || []` *inside* that guard, where it can never run. Since the tag
  // manager injects Inspectlet asynchronously and init itself waits for the catalogue, everything
  // raised before that point was dropped - exactly the bootstrap window in which the interesting
  // failures happen.
  //
  private queue(): unknown[]
  {
    window.__insp = window.__insp ?? [];
    return window.__insp;
  }

  private tagSession(payload: Record<string, unknown> | string): void
  {
    this.queue().push(['tagSession', payload]);
  }
}
